<template>
  <div v-if="visible" class="announcement-banner">
    <div class="banner-content">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="banner-icon">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/>
      </svg>
      <div class="banner-text">
        <span class="banner-title">{{ pinned!.title }}</span>
        <span class="banner-sep">—</span>
        <span class="banner-body">{{ pinned!.content }}</span>
      </div>
    </div>
    <button class="banner-close" @click="dismiss" aria-label="닫기">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { collection, query, where, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "@/firebase";

interface Announcement {
  id: string;
  title: string;
  content: string;
  isPinned: boolean;
}

const STORAGE_PREFIX = "ck_dismissed_announcement_";

const pinned = ref<Announcement | null>(null);
const dismissed = ref(false);
let unsub: Unsubscribe | null = null;

const visible = computed(() => !!pinned.value && !dismissed.value);

function dismiss() {
  if (pinned.value) {
    localStorage.setItem(STORAGE_PREFIX + pinned.value.id, "1");
  }
  dismissed.value = true;
}

onMounted(() => {
  const q = query(collection(db, "announcements"), where("isPinned", "==", true));
  unsub = onSnapshot(q, (snap) => {
    if (snap.empty) {
      pinned.value = null;
      dismissed.value = false;
      return;
    }
    const doc = snap.docs[0];
    const item = { id: doc.id, ...doc.data() } as Announcement;
    pinned.value = item;
    // Check if this announcement was already dismissed
    dismissed.value = localStorage.getItem(STORAGE_PREFIX + item.id) === "1";
  });
});

onUnmounted(() => unsub?.());
</script>

<style scoped>
.announcement-banner {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 20px;
  background: var(--coral);
  color: #fff;
  box-shadow: 0 2px 8px rgba(255,92,53,0.3);
}

.banner-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.banner-icon {
  flex-shrink: 0;
  opacity: 0.9;
}

.banner-text {
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.banner-title {
  font-weight: 700;
}

.banner-sep {
  margin: 0 6px;
  opacity: 0.6;
}

.banner-body {
  opacity: 0.9;
}

.banner-close {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}

.banner-close:hover {
  background: rgba(255,255,255,0.25);
}
</style>
